import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../lib/settings";
import { toast } from "sonner";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.92a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.35z"/>
  </svg>
);

const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z"/>
  </svg>
);

const PinterestIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.43.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.77c0-1.66.96-2.9 2.16-2.9 1.02 0 1.51.77 1.51 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.17 1.78 2.17 2.14 0 3.78-2.25 3.78-5.51 0-2.88-2.07-4.9-5.03-4.9-3.43 0-5.44 2.57-5.44 5.22 0 1.04.4 2.15.9 2.75.1.12.11.22.08.34-.09.36-.29 1.2-.33 1.37-.05.22-.17.27-.4.16-1.49-.7-2.42-2.87-2.42-4.62 0-3.76 2.73-7.22 7.88-7.22 4.14 0 7.35 2.95 7.35 6.89 0 4.11-2.59 7.42-6.18 7.42-1.21 0-2.35-.63-2.74-1.37l-.74 2.83c-.27 1.04-1 2.34-1.49 3.13A12 12 0 1 0 12 0z"/>
  </svg>
);

const YoutubeIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.5 12 3.5 12 3.5s-7.6 0-9.4.6A3 3 0 0 0 .5 6.2C0 8 0 12 0 12s0 4 .5 5.8a3 3 0 0 0 2.1 2.1c1.8.6 9.4.6 9.4.6s7.6 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 16 24 12 24 12s0-4-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z"/>
  </svg>
);


const COLS = [
  {
    title: "Customer Care",
    links: [
      { label: "Contact", to: "/info/contact" },
      { label: "FAQ", to: "/info/faq" },
      { label: "Shipping & Returns", to: "/info/shipping" },
      { label: "Order Lookup", to: "/orders" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Custom Prompts", to: "/info/contact" },
      { label: "Brand Collaborations", to: "/info/contact" },
      { label: "Wholesale", to: "/info/contact" },
      { label: "Press", to: "/info/contact" },
    ],
  },
  {
    title: "About TTFM",
    links: [
      { label: "Our Story", to: "/info/about" },
      { label: "The Brand", to: "/about" },
      { label: "Sustainability", to: "/info/about" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/info/privacy" },
      { label: "Terms of Service", to: "/info/terms" },
      { label: "Cookie Settings", to: "/info/privacy" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();

  async function subscribe(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to the inner circle.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe. Try a different email.");
    } finally { setLoading(false); }
  }

  return (
    <footer data-testid="site-footer" className="relative mt-24 bg-[#FDFBF7]">
      {/* Newsletter band */}
      <div className="bg-[#2C2924] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6">
            <div className="font-serif text-3xl gold-heading">Stay close.</div>
            <p className="mt-2 text-[#F3E5AB]/80 text-sm max-w-md">
              Quiet emails about new prompts, ebooks, beauty drops, and members-only discounts.
            </p>
          </div>
          <form onSubmit={subscribe} className="md:col-span-6 flex gap-2 max-w-md md:ml-auto" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="flex-1 px-5 py-3 rounded-full bg-[#1A1815] border-2 border-[var(--accent)]/40 focus:border-[var(--accent)] focus:outline-none text-sm text-[#FDFBF7] placeholder:text-[#F3E5AB]/40" />
            <button type="submit" disabled={loading} data-testid="newsletter-submit"
              className="px-6 py-3 rounded-full bg-[var(--accent)] text-black font-semibold text-sm disabled:opacity-60">
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      {/* Main link columns */}
      <div className="bg-[#F5F2EA] border-y border-[#EAE4D3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
          {COLS.map((col) => (
            <div key={col.title}>
              <div className="font-serif uppercase text-sm tracking-[0.25em] gold-heading font-bold mb-5">
                {col.title}
              </div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-[#5C5547] hover:text-[var(--accent)] hover:underline underline-offset-4 transition">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <div className="font-serif uppercase text-sm tracking-[0.25em] gold-heading font-bold mb-5">
              Follow Us
            </div>
            <ul className="space-y-3 text-sm text-[#5C5547]">
              {settings.instagram_main && (
                <li>
                  <a href={`https://instagram.com/${(settings.instagram_main || "").replace("@", "")}`}
                    target="_blank" rel="noreferrer" data-testid="footer-ig-main"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <Instagram className="w-4 h-4" /> {settings.instagram_main}
                  </a>
                </li>
              )}
              {settings.instagram_secondary && (
                <li>
                  <a href={`https://instagram.com/${(settings.instagram_secondary || "").replace("@", "")}`}
                    target="_blank" rel="noreferrer" data-testid="footer-ig-secondary"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <Instagram className="w-4 h-4" /> {settings.instagram_secondary}
                  </a>
                </li>
              )}
              {settings.instagram_personal && (
                <li>
                  <a href={`https://instagram.com/${(settings.instagram_personal || "").replace("@", "")}`}
                    target="_blank" rel="noreferrer" data-testid="footer-ig-personal"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <Instagram className="w-4 h-4" /> {settings.instagram_personal}
                  </a>
                </li>
              )}
              {settings.facebook_url && (
                <li>
                  <a href={settings.facebook_url} target="_blank" rel="noreferrer" data-testid="footer-fb-link"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <FacebookIcon className="w-4 h-4" /> Facebook
                  </a>
                </li>
              )}
              {settings.tiktok_url && (
                <li>
                  <a href={settings.tiktok_url} target="_blank" rel="noreferrer" data-testid="footer-tiktok-link"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <TikTokIcon className="w-4 h-4" /> TikTok
                  </a>
                </li>
              )}
              {settings.pinterest_url && (
                <li>
                  <a href={settings.pinterest_url} target="_blank" rel="noreferrer" data-testid="footer-pinterest-link"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <PinterestIcon className="w-4 h-4" /> Pinterest
                  </a>
                </li>
              )}
              {settings.youtube_url && (
                <li>
                  <a href={settings.youtube_url} target="_blank" rel="noreferrer" data-testid="footer-youtube-link"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <YoutubeIcon className="w-4 h-4" /> YouTube
                  </a>
                </li>
              )}
              {settings.shopify_store_url && (
                <li>
                  <a href={settings.shopify_store_url} target="_blank" rel="noreferrer" data-testid="footer-shopify-link"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <ShoppingBag className="w-4 h-4" /> Shopify Store
                  </a>
                </li>
              )}
              {settings.etsy_url && (
                <li>
                  <a href={settings.etsy_url} target="_blank" rel="noreferrer" data-testid="footer-etsy-link"
                    className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                    <ShoppingBag className="w-4 h-4" /> Etsy Shop
                  </a>
                </li>
              )}
              <li>
                <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 hover:text-[var(--accent)]">
                  <Mail className="w-4 h-4" /> {settings.contact_email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Wordmark + copyright */}
      <div className="bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="font-serif uppercase tracking-[0.35em] text-2xl text-[#1A1815] font-semibold">{settings.brand_name}</div>
          <div className="mx-auto mt-4 h-px w-16 gold-line" />
          <div className="mt-6 text-xs text-[#5C5547] tracking-wider">
            © {new Date().getFullYear()} {settings.brand_name}. All rights reserved. Curated with care.
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#5C5547]">
            <Link to="/info/privacy" className="hover:text-[var(--accent)]">Privacy</Link>
            <span className="mx-3">·</span>
            <Link to="/info/terms" className="hover:text-[var(--accent)]">Terms</Link>
            <span className="mx-3">·</span>
            <Link to="/orders" className="hover:text-[var(--accent)]">Order Lookup</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
